import { useEffect, useMemo, useState } from 'react';
import type { Key } from 'react';
import { Button, Card, Empty, Flex, Input, Skeleton, Splitter, Tag, Tree, Typography, theme } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router-dom';
import { DocumentViewer } from '@/components/DocumentViewer';
import { useGetAssessmentApplicantsQuery } from '@/pages/application/credit-analyst/pages/calculations/apis/calculationApi';
import { useListPortalDocumentsQuery } from '@/pages/application/credit-analyst/pages/portal-documents/apis/portalDocumentsApi';
import { usePortalDocumentPreview } from '@/pages/application/credit-analyst/pages/portal-documents/hooks/usePortalDocumentPreview';
import {
  applicantLabels,
  buildPortalExplorerTree,
  portalDocumentName,
} from '@/pages/application/credit-analyst/pages/portal-documents/utils/portal-documents.util';
import styles from './DocumentExplorer.module.scss';

const { DirectoryTree } = Tree;

/**
 * Spec §4.5: the portal (document_classification) counterpart of DocumentExplorer, opened in the
 * same popup window. DocumentExplorer and its /:dealId route are not touched.
 */
const PortalDocumentExplorer = () => {
  const { token } = theme.useToken();
  const { assessmentId = '' } = useParams<{ assessmentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const selectedId = searchParams.get('docId');

  const list = useListPortalDocumentsQuery(assessmentId, { skip: !assessmentId });
  const { data: applicantsRes } = useGetAssessmentApplicantsQuery({ assessmentId }, { skip: !assessmentId });
  const labels = useMemo(() => applicantLabels(applicantsRes?.data ?? []), [applicantsRes]);
  // Memoised: a fresh [] each render would rebuild `tree`, and the expand effect below would loop.
  const docs = useMemo(() => list.data ?? [], [list.data]);
  const tree = useMemo(() => buildPortalExplorerTree(docs, labels, search), [docs, labels, search]);
  const selected = docs.find((d) => d.documentId === selectedId) ?? null;
  const preview = usePortalDocumentPreview(selected?.documentId ?? null);

  const [expanded, setExpanded] = useState<Key[]>([]);
  useEffect(() => {
    // Open every applicant and type node whenever the tree's shape changes (load or search).
    setExpanded(tree.flatMap((a) => [a.key, ...(a.children ?? []).map((t) => t.key)]));
  }, [tree]);

  const select = (documentId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (documentId) next.set('docId', documentId);
    else next.delete('docId');
    setSearchParams(next, { replace: true });
  };

  const treeData = tree.map((a) => ({
    key: a.key,
    title: (
      <Flex gap={6} align="center">
        <Typography.Text strong>{a.title}</Typography.Text>
        <Tag>{a.count}</Tag>
      </Flex>
    ),
    children: (a.children ?? []).map((t) => ({
      key: t.key,
      title: (
        <Flex gap={6} align="center">
          <Typography.Text>{t.title}</Typography.Text>
          <Tag>{t.count}</Tag>
        </Flex>
      ),
      children: (t.children ?? []).map((l) => ({ key: l.key, title: l.title, isLeaf: true })),
    })),
  }));

  return (
    <Card style={{ borderRadius: 12, height: '100vh', overflow: 'hidden' }} styles={{ body: { padding: 0, height: '100%' } }}>
      <Splitter style={{ height: '100%', background: token.colorBgContainer }}>
        <Splitter.Panel collapsible defaultSize="30%" min="20%" max="50%" style={{ borderRight: `1px solid ${token.colorBorderSecondary}`, overflow: 'hidden' }}>
          <div className={styles.docPanel}>
            <div className={styles.panelHeader}>
              <Flex align="center" gap={8}>
                <Typography.Text strong>Portal documents</Typography.Text>
                <Tag>{docs.length}</Tag>
              </Flex>
            </div>
            <div className={styles.filterBar}>
              <Input allowClear prefix={<SearchOutlined />} placeholder="Search files…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className={styles.treeWrap}>
              {list.isLoading ? (
                <Skeleton active />
              ) : tree.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={list.isError ? "Couldn't load portal documents" : 'No documents'} />
              ) : (
                <DirectoryTree
                  treeData={treeData}
                  expandedKeys={expanded}
                  onExpand={(keys) => setExpanded(keys)}
                  selectedKeys={selectedId ? [`d:${selectedId}`] : []}
                  onSelect={(_keys, info) => {
                    const key = String(info.node.key);
                    if (key.startsWith('d:')) select(key.slice(2));
                  }}
                />
              )}
            </div>
          </div>
        </Splitter.Panel>
        <Splitter.Panel style={{ position: 'relative', background: token.colorBgLayout }}>
          <div style={{ height: '100%' }}>
            {selected ? (
              <Flex vertical style={{ height: '100%' }}>
              {preview.isError && selected.driveWebViewLink && (
                <Flex justify="center" style={{ padding: 8 }}>
                  <Button href={selected.driveWebViewLink} target="_blank" rel="noopener noreferrer">
                    Open in Google Drive
                  </Button>
                </Flex>
              )}
              <div style={{ flex: 1, minHeight: 0 }}>
              <DocumentViewer
                key={selected.documentId}
                url={preview.url}
                isLoading={preview.isLoading}
                fileName={preview.fileName ?? portalDocumentName(selected)}
                contentType={preview.contentType ?? selected.contentType}
                onClose={() => select(null)}
                detail={preview.detail}
                emptyDescription={preview.isError ? "Couldn't load a preview for this document." : "A preview isn't available for this document yet."}
              />
              </div>
              </Flex>
            ) : (
              <div className={styles.emptyPreview}>
                <Typography.Text type="secondary">Select a document from the panel to preview it here</Typography.Text>
              </div>
            )}
          </div>
        </Splitter.Panel>
      </Splitter>
    </Card>
  );
};

export default PortalDocumentExplorer;
